import * as THREE from 'three';
import { generateId } from '../utils/helpers';

import { CurtainWallElement } from '../core/types';
import { BaseTool } from './BaseTool';

export interface CurtainWallProperties {
    height: number;
    baseOffset: number;
    topOffset: number;
    baseLevelId: string;
    topLevelId: string;
    gridLayout: 'None' | 'Fixed Distance' | 'Fixed Number' | 'Maximum Spacing';
    verticalGridSpacing?: number;
    horizontalGridSpacing?: number;
}

/**
 * Interactive placement tool for curtain walls: two picks define the wall
 * baseline; the element is added to the BIM store and rendered by
 * CurtainWallSolver through the 'Curtain Walls' renderer case.
 */
export class CurtainWallPlacementTool extends BaseTool {
    id = 'curtain_wall';
    name = 'Curtain Wall';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;

    onPointerDown(event: any): void {
        if (!this.context || event.button !== 0) return;
        const point = event.point as THREE.Vector3 | undefined;
        if (!point) return;

        if (!this.startPoint) {
            this.startPoint = point.clone();
        } else {
            this.placeWall(this.startPoint, point.clone());
            this.startPoint = null;
        }
    }

    onPointerMove(): void { }
    onPointerUp(): void { }

    private placeWall(start: THREE.Vector3, end: THREE.Vector3) {
        const element = createCurtainWall(start, end, {
            height: 3.5,
            baseOffset: 0,
            topOffset: 0,
            baseLevelId: '',
            topLevelId: '',
            gridLayout: 'Fixed Distance',
            verticalGridSpacing: 1.5,
        });
        this.context!.bimStore.addElement(element);
    }
}

export const createCurtainWall = (
    startPoint: THREE.Vector3,
    endPoint: THREE.Vector3,
    properties: CurtainWallProperties
): CurtainWallElement => {
    // Generate Grid
    const length = startPoint.distanceTo(endPoint);
    const height = properties.height;

    // Simplified grid generation
    const verticalGrids = [];
    if (properties.gridLayout === 'Fixed Distance' && properties.verticalGridSpacing) {
        const numPanels = Math.floor(length / properties.verticalGridSpacing);
        for (let i = 1; i <= numPanels; i++) {
            verticalGrids.push(i * properties.verticalGridSpacing);
        }
    }

    return {
        id: generateId(),
        category: 'Curtain Walls',
        type: 'Curtain Wall',
        parameters: {
            ...properties
        },
        constraints: [],
        geometryData: {
            start: [startPoint.x, startPoint.y, startPoint.z],
            end: [endPoint.x, endPoint.y, endPoint.z],
            height: height,
            verticalGrids: verticalGrids,
            horizontalGrids: [] // Placeholder
        }
    };
};
